import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router';
import { Domain } from '@tiendanube/common/src/enums';
import { CheckCircleIcon } from '@tiendanube/icons';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { CATALOG_UPLOAD_VIDEOS } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { ActionProp, useResponsive } from 'commons/components';
import { useToast, useDocumentTitle } from 'commons/hooks';
import { useGetLanguage, useHasShippingMultiCD } from 'domains/Auth/hooks';
import { CATALOG_ROUTES } from 'domains/Catalog/catalogRoutes';
import AlertCreateProductsAndVariantsMetafields from 'domains/Metafields/components/Alerts/AlertCreateProductsAndVariantsMetafields';
import { productDefault } from './constants';
import { createProductDigest } from './productFormUtils';
import {
  trackingProductSaveClick,
  trackingProductSaveResult,
} from '../../tracking';
import { FormProduct, ProductPage } from '../components';
import {
  useCreateProduct,
  useProductForm,
  useFetchProductVariantsMetafields,
} from '../hooks';
import { ProductFormState } from '../hooks/useProductForm/types';

function NewProductPage(): JSX.Element {
  const { selectFirstOptionMetafields } = useFetchProductVariantsMetafields();
  const { isDesktop, isMobile } = useResponsive();

  const hasShippingMultiCD = useHasShippingMultiCD();
  const history = useHistory();
  const language = useGetLanguage();
  const [hasUploadVideos] = useHasTags([CATALOG_UPLOAD_VIDEOS]);

  const { t } = useTranslation(Domain.CATALOG);

  const { addProduct } = useCreateProduct();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();

  const title = t('products.newProduct');
  useDocumentTitle(title);

  const onCreateProduct = async (data: Partial<ProductFormState>) => {
    addToastProgress({
      label: t('products.addingProduct'),
    });

    const params = createProductDigest({
      product: data,
      language,
      hasShippingMultiCD,
      hasMedia: hasUploadVideos,
    });

    const newProduct = await addProduct(params);

    history.replace(
      generatePath(CATALOG_ROUTES.editProduct, { id: newProduct.id }),
    );
  };

  const onSuccess = () => {
    trackingProductSaveResult({ mode: 'create', result: 'success' });

    closeToastProgress();
    addToast({
      label: t('products.toast.created'),
      appearance: 'success',
    });
  };

  const onError = () => {
    trackingProductSaveResult({ mode: 'create', result: 'error' });
    closeToastProgress();
    addToast({
      label: t('products.toast.createdError'),
      appearance: 'danger',
    });
  };

  const {
    values,
    errors,
    isDirty,
    isSaving,
    isFormSubmitted,
    showModalNullStockVariant,
    handleCloseModalNullStockVariant,
    handleConfirmModalNullVaritantStock,
    handleChange,
    handleChangeCategories,
    handleChangeImages,
    handleChangeVariants,
    handleChangeAttributes,
    handleSave,
    handleChangeToAllVariants,
    handleChangeSectionCodes,
    handleChangeToAllMetafieldsVariants,
    handleChangeRelatedProducts,
    handleChangeInvoice,
  } = useProductForm({
    initialValues: productDefault,
    selectFirstOptionMetafields,
    onSave: onCreateProduct,
    onError,
    onSuccess,
  });

  const handleSaveWithTracking = () => {
    trackingProductSaveClick({ mode: 'create' });
    handleSave();
  };

  const handleCancel = () => {
    history.goBack();
  };

  const actions: ActionProp[] = [
    ...(isDesktop
      ? [
          {
            children: t('products.detail.cancel'),
            onClick: handleCancel,
          },
        ]
      : []),
    {
      children: isDesktop
        ? t('products.detail.save')
        : t('products.detail.header.save'),
      onClick: handleSaveWithTracking,
      appearance: isDesktop ? 'primary' : undefined,
      icon: isMobile ? CheckCircleIcon : undefined,
      spinner: isSaving,
      disabled: isSaving,
      isPrimary: true,
    },
  ];

  return (
    <ProductPage
      isSaving={isSaving}
      handleSave={handleSave}
      actions={actions}
      title={title}
      isDirty={isDirty}
      showModalNullStockVariant={showModalNullStockVariant}
      handleCloseModalNullStockVariant={handleCloseModalNullStockVariant}
      handleConfirmModalNullVaritantStock={handleConfirmModalNullVaritantStock}
      isEdit={false}
    >
      <AlertCreateProductsAndVariantsMetafields />
      <FormProduct
        errors={errors}
        values={values}
        isFormSubmitted={isFormSubmitted}
        handleChange={handleChange}
        handleChangeCategories={handleChangeCategories}
        handleChangeImages={handleChangeImages}
        handleChangeVariants={handleChangeVariants}
        handleChangeAttributes={handleChangeAttributes}
        handleChangeToAllVariants={handleChangeToAllVariants}
        handleChangeSectionCodes={handleChangeSectionCodes}
        handleChangeToAllMetafieldsVariants={
          handleChangeToAllMetafieldsVariants
        }
        handleChangeRelatedProducts={handleChangeRelatedProducts}
        handleChangeInvoice={handleChangeInvoice}
      />
    </ProductPage>
  );
}

export default NewProductPage;
